import { zodResolver } from "@hookform/resolvers/zod";
import { Deferred, Head, router, usePage } from "@inertiajs/react";
import { ChevronDownIcon, Info } from "lucide-react";
import {
	type ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AdminPicMultiSelector from "@/components/admin-portal/appointment/form/admin-pic-multi-selector";
import {
	PIC_SLOT_KEYS,
	PicSlotSelectors,
} from "@/components/admin-portal/appointment/form/pic-slot-selectors";
import {
	AdditionalSettingsForm,
	AppointmentSchedulingForm,
	DraftBaselineContext,
	FinalStep,
	FormContainer,
	FormProvider,
	PatientDetailsForm,
	ReviewForm,
	StepButtons,
	useFormProvider,
} from "@/components/admin-portal/appointment/new-appointment-form";
import {
	FormPageContainer,
	FormPageHeaderGridPattern,
} from "@/components/admin-portal/shared/page-layout";
import {
	Step,
	type StepItem,
	Stepper,
	useStepper,
} from "@/components/shared/stepper";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import {
	DRAFT_STEPS,
	useAppointmentDraft,
} from "@/hooks/admin-portal/appointment/use-appointment-draft";
import {
	requestDraftStepRestore,
	useEnsureServiceList,
	useFormReset,
} from "@/hooks/admin-portal/appointment/use-appointment-form";
import type {
	LocationOption,
	TherapistOption,
} from "@/hooks/admin-portal/appointment/use-appointment-utils";
import { DraftAutosaveProvider } from "@/hooks/admin-portal/appointment/use-draft-autosave";
import { useNavigationGuard } from "@/hooks/admin-portal/appointment/use-navigation-guard";
import { useIsMobile } from "@/hooks/use-mobile";
import {
	type AppointmentBookingSchema,
	buildAppointmentPayload,
	buildBookingSchema,
	type DraftBaseline,
} from "@/lib/appointments/form";
import type { ReasonOption } from "@/lib/appointments/reason-label";
import type {
	FISIOHOME_PARTNER,
	GENDERS,
	PATIENT_CONDITIONS_WITH_DESCRIPTION,
	PATIENT_REFERRAL_OPTIONS,
	PREFERRED_THERAPIST_GENDER,
} from "@/lib/constants";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { Package } from "@/types/admin-portal/package";
import type { Patient } from "@/types/admin-portal/patient";
import type { Service } from "@/types/admin-portal/service";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";

type ServiceOption = Pick<
	Service,
	"id" | "name" | "code" | "active" | "description"
> & {
	packages: Pick<
		Package,
		"id" | "name" | "active" | "numberOfVisit" | "totalPrice" | "currency"
	>[];
};

type AdminOption = {
	id: string;
	name: string;
	email?: string;
};

interface StepperProps extends StepItem {
	component: ReactElement;
}

export interface AppointmentNewProps {
	services?: ServiceOption[];
	locations?: LocationOption[];
	therapists?: TherapistOption[];
	admins?: AdminOption[];
	optionsData?: {
		patientGenders: typeof GENDERS;
		preferredTherapistGender: typeof PREFERRED_THERAPIST_GENDER;
		referralSources: Array<(typeof PATIENT_REFERRAL_OPTIONS)[number]["value"]>;
		patientConditions: typeof PATIENT_CONDITIONS_WITH_DESCRIPTION;
		fisiohomePartnerNames: Array<(typeof FISIOHOME_PARTNER)[number]["value"]>;
	};
	patientList?: Patient[];
	patientContactList?: NonNullable<Patient["contact"]>[];
	// The stored contact a typed new contact's number or email would be linked to.
	matchingContact?: NonNullable<Patient["contact"]> | null;
	// The picked existing patient, re-read by id when the booking tab is shown again.
	selectedPatient?: Patient | null;
	appointment: Appointment;
	appointmentReference: Appointment | null;
	// Mirrors AppointmentDraft::MITRA_ASSIGNMENT_REASONS, which is the source of truth.
	// Declared here rather than read off the global props, whose index signature would
	// let a misspelled prop name through typecheck.
	mitraAssignmentReasons?: ReasonOption[];
	// Governs the three KPI slot selectors above the PIC multi-select — see issue #131.
	draftPicKpiEnabled?: boolean;
}

export interface AppointmentNewGlobalPageProps
	extends BaseGlobalPageProps,
		AppointmentNewProps {
	[key: string]: any;
}

function FormComponent() {
	const { url: pageURL, props: globalProps } =
		usePage<AppointmentNewGlobalPageProps>();
	const isMobile = useIsMobile();
	const { t: taf } = useTranslation("appointments-form");
	const { isSuccessBooked, mode, formStorage, setFormStorage } =
		useFormProvider();
	const [isLoading, setIsLoading] = useState(false);
	const [draftLoaded, setDraftLoaded] = useState(false);
	const [draftBaseline, setDraftBaseline] = useState<DraftBaseline>({});

	// Get draftId from URL for use in loadDraft callback
	const draftIdFromUrl = useMemo(() => {
		return new URLSearchParams(window.location.search).get("draft_id");
	}, []);

	// Load draft if draftId is in URL - Now integrated into useAppointmentDraft
	useAppointmentDraft({
		draftIdFromUrl,
		setFormStorage,
		setDraftLoaded,
		onDraftLoaded: (_formData, currentStep, baseline) => {
			// What the customer submitted, kept by the server (docs/adr/0013), so every tab
			// and the booking check compare against the same thing. A customer can pick a
			// Mitra in their own app, where no reason is ever asked for, so the reason is
			// owed only once the admin puts somebody different on a Visit — see
			// mitraReasonFieldShown. A Draft an admin started has none: every Mitra on it
			// was an admin's call. Set before the form resets onto the Draft, so the very
			// first validation already compares against it.
			setDraftBaseline(baseline ?? {});

			if (currentStep) {
				// Once per (tab, Draft) — see requestDraftStepRestore. This callback runs
				// again after every autosave and refresh, and a re-armed request would drag
				// the admin back here after they had already navigated elsewhere.
				requestDraftStepRestore(
					draftIdFromUrl,
					DRAFT_STEPS.indexOf(currentStep as (typeof DRAFT_STEPS)[number]),
				);
			}
		},
		onError: (error) => {
			console.error("Failed to load draft:", error);
		},
	});

	// stepper management state - reordered to prioritize scheduling first
	const steps = useMemo(
		() =>
			[
				{
					label: taf("stepper.patient_profile.label"),
					description: taf("stepper.patient_profile.description"),
					component: <PatientDetailsForm />,
				},
				{
					label: taf("stepper.appt_settings.label"),
					description: taf("stepper.appt_settings.description"),
					component: <AppointmentSchedulingForm />,
				},
				{
					label: taf("stepper.additional_settings.label"),
					description: taf("stepper.additional_settings.description"),
					component: <AdditionalSettingsForm />,
				},
				{
					label: taf("stepper.review.label"),
					description: taf("stepper.review.description"),
					component: <ReviewForm />,
				},
			] satisfies StepperProps[],
		[taf],
	);

	// form management state
	const pageHeader = useMemo(() => {
		const appRef = globalProps?.appointmentReference;
		const regNumber = appRef?.registrationNumber;
		const currentVisit = appRef?.seriesAppointments?.length
			? Math.max(...appRef.seriesAppointments.map((a) => a.visitNumber)) + 1
			: 1;
		const maxVisit = appRef?.totalPackageVisits;

		return {
			title: mode === "new" ? taf("page_title.new") : taf("page_title.series"),
			description:
				mode === "new"
					? taf("page_description.new")
					: taf("page_description.series"),
			regNumber,
			series:
				mode === "series" ? `Visit ${currentVisit}/${maxVisit}` : undefined,
		};
	}, [mode, globalProps?.appointmentReference, taf]);
	// Rebuilt when a Draft loads so the Mitra rule knows which assignment the admin
	// inherited rather than made. The flag has to reach the schema too, not just the
	// field: a rule left on over a field that is no longer rendered blocks the submit
	// with an error the admin has no way to clear.
	const mitraReasonEnabled =
		globalProps?.adminPortal?.featureFlags?.mitraAssignmentReasonEnabled ??
		false;
	const bookingSchema = useMemo(
		() => buildBookingSchema(draftBaseline, mitraReasonEnabled),
		[draftBaseline, mitraReasonEnabled],
	);
	const form = useForm<AppointmentBookingSchema>({
		resolver: zodResolver(bookingSchema),
		defaultValues: formStorage || {
			formOptions: {},
			contactInformation: {},
			patientDetails: {},
			appointmentScheduling: {},
			additionalSettings: {},
		},
		mode: "onChange",
	});

	// Reset form when draft is loaded - Extracted to custom hook
	useFormReset({
		form,
		formStorage,
		draftLoaded,
	});
	// Idempotency guard: `handleSubmit` runs Zod validation asynchronously, so the
	// submit button's `disabled={isLoading}` doesn't engage until `onStart` fires
	// — leaving a window where a rapid double-click (or Enter + click) submits the
	// booking twice. The backend booking POST has no idempotency key, so two POSTs
	// create two bookings. This ref short-circuits any submit while one is in flight.
	const isSubmittingRef = useRef(false);
	const onSubmit = useCallback(
		(values: AppointmentBookingSchema) => {
			if (isSubmittingRef.current) return; // a submit is already in flight
			isSubmittingRef.current = true;
			setIsLoading(true); // disable the button now, not only on onStart

			console.group();
			console.log("Starting process to saving the appointment booking...");

			// prepare the payload
			const submitURL =
				globalProps.adminPortal.router.adminPortal.appointment.book;
			const submitConfig = {
				preserveScroll: true,
				preserveState: true,
				replace: false,
				only: ["adminPortal", "flash", "errors", "appointment"],
				onStart: () => setIsLoading(true),
				onFinish: () => {
					isSubmittingRef.current = false;
					setTimeout(() => setIsLoading(false), 250);
				},
			} satisfies Parameters<typeof router.put>["2"];
			const payload = buildAppointmentPayload(values);
			router.post(submitURL, { appointment: payload }, submitConfig);

			console.log("Appointment booking successfully saved...");
			console.groupEnd();
		},
		[globalProps.adminPortal.router.adminPortal.appointment.book],
	);

	/**
	 * Navigation guard - Extracted to custom hook
	 * Handles route change confirmation and storage cleanup
	 */
	useEnsureServiceList(form.control);
	const [activeStep, setActiveStep] = useState(0);
	const [isNavigateConfirm, setIsNavigateConfirm] = useState(false);
	useNavigationGuard({
		globalProps,
		pageURL,
		isSuccessBooked,
		isNavigateConfirm,
		setIsNavigateConfirm,
	});

	return (
		<section className="flex flex-col gap-4 mx-auto md:gap-6">
			<FormPageHeaderGridPattern {...pageHeader} />

			<Form {...form}>
				{/* A rejected submit is reported by useStepButtons, which watches
				    formState.submitCount. It has to live there rather than in an onInvalid
				    here: the errors that block a submit render inline on the step that owns
				    them, the admin is on Review by then, and only a hook inside the Stepper
				    can navigate them to that step. Without it the button simply does
				    nothing. */}
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<p className="mb-4 flex items-start gap-2 text-xs text-muted-foreground text-pretty">
						<Info className="mt-px size-3.5 shrink-0 text-primary" />
						{taf("draft.auto_save_info")}
					</p>

					{/* Who answers for the Draft rarely changes once L1 is the admin creating it,
					    so it folds to one line instead of sitting above every step. */}
					<Collapsible className="mb-4 border shadow-inner border-border rounded-xl bg-background">
						<CollapsibleTrigger className="group flex w-full items-center gap-3 p-4 text-left text-sm md:px-6 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
							<span className="font-medium shrink-0">
								{taf("draft.pic_summary.title")}
							</span>
							<PicSummary
								control={form.control}
								admins={globalProps.admins || []}
								slotsEnabled={!!globalProps.draftPicKpiEnabled}
							/>
							<ChevronDownIcon className="ml-auto size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
						</CollapsibleTrigger>
						<CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
							<div className="grid gap-4 px-4 pb-4 md:px-6 md:pb-6">
								{/* Admin PIC Selector for draft feature */}
								<Deferred
									data={["admins"]}
									fallback={
										<Skeleton className="w-full rounded-md col-span-full h-20" />
									}
								>
									{/* Above the multi-select, which stays as it is for supporting PICs:
							    the slots answer "who is accountable for which stage", the list
							    answers "who else can see this Draft". */}
									{globalProps.draftPicKpiEnabled && (
										<div className="col-span-full">
											<PicSlotSelectors
												// Matches AdminPicMultiSelector's own trigger right below it.
												triggerClassName="bg-sidebar shadow-inner"
												admins={globalProps.admins || []}
												value={{
													picL1: form.watch("additionalSettings.picL1") ?? null,
													picL2: form.watch("additionalSettings.picL2") ?? null,
													picL3: form.watch("additionalSettings.picL3") ?? null,
												}}
												onChange={(slots) => {
													for (const key of PIC_SLOT_KEYS) {
														form.setValue(
															`additionalSettings.${key}`,
															slots[key],
															{
																shouldDirty: true,
															},
														);
													}
												}}
											/>
										</div>
									)}

									<div className="col-span-full">
										<AdminPicMultiSelector showDescription={true} />
									</div>
								</Deferred>
							</div>
						</CollapsibleContent>
					</Collapsible>

					<DraftAutosaveProvider>
						<DraftBaselineContext.Provider value={draftBaseline}>
							<Stepper
								// scrollTracking
								variant={isMobile ? "circle" : "circle-alt"}
								size="sm"
								orientation="vertical"
								initialStep={0}
								steps={steps}
							>
								<ActiveStepTracker onChange={setActiveStep} />
								{steps.map((stepProps, index) => (
									<Step
										key={stepProps.label}
										{...stepProps}
										// Going back to a finished step is what Prev does, one header
										// at a time; going forward still has to pass Save & Next.
										onClickStep={
											index < activeStep
												? (step, setStep) => setStep(step)
												: undefined
										}
									>
										<FormContainer>
											{stepProps.component}

											<StepButtons isFormLoading={isLoading} />
										</FormContainer>
									</Step>
								))}

								{isSuccessBooked && <FinalStep />}
							</Stepper>
						</DraftBaselineContext.Provider>
					</DraftAutosaveProvider>
				</form>
			</Form>
		</section>
	);
}

/** Lifts the Stepper's current step to the page, which renders the step headers. */
function ActiveStepTracker({ onChange }: { onChange: (step: number) => void }) {
	const { activeStep } = useStepper();
	useEffect(() => onChange(activeStep), [activeStep, onChange]);
	return null;
}

/** "L1 Ayu · L2 — · L3 — · +2" — the PIC block folded to one line. */
function PicSummary({
	control,
	admins,
	slotsEnabled,
}: {
	control: ReturnType<typeof useForm<AppointmentBookingSchema>>["control"];
	admins: { id: string | number; name: string }[];
	slotsEnabled: boolean;
}) {
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "draft.pic_summary",
	});
	const [picL1, picL2, picL3, supporting] = useWatch({
		control,
		name: [
			"additionalSettings.picL1",
			"additionalSettings.picL2",
			"additionalSettings.picL3",
			"additionalSettings.admins",
		],
	});
	// The list also carries the slot holders; the multi-select hides them, so do we.
	const slotHolders = [picL1, picL2, picL3].filter(Boolean).map(String);
	const supportingCount =
		supporting?.filter((admin) => !slotHolders.includes(String(admin.id)))
			.length ?? 0;
	const nameOf = (id?: string | null) =>
		admins.find((admin) => String(admin.id) === String(id))?.name ?? "—";

	// Without the KPI slots there is no L1/L2/L3 to report, only the PICs picked.
	if (!slotsEnabled) {
		return (
			<span className="min-w-0 truncate text-muted-foreground">
				{supporting?.map((admin) => admin.name).join(", ") || "—"}
			</span>
		);
	}

	return (
		<span className="min-w-0 truncate text-muted-foreground">
			{[
				`L1 ${nameOf(picL1)}`,
				`L2 ${nameOf(picL2)}`,
				`L3 ${nameOf(picL3)}`,
				supportingCount ? t("supporting", { count: supportingCount }) : null,
			]
				.filter(Boolean)
				.join(" · ")}
		</span>
	);
}

export default function AppointmentNew(_props: AppointmentNewProps) {
	const { t: taf } = useTranslation("appointments-form");

	return (
		<FormProvider>
			<Head title={taf("head_title")}>
				<link
					rel="stylesheet"
					type="text/css"
					href="https://js.api.here.com/v3/3.1/mapsjs-ui.css"
				/>
			</Head>

			<FormPageContainer>
				<FormComponent />
			</FormPageContainer>
		</FormProvider>
	);
}
